<script setup lang="ts">
import { useSeo } from '@/composables/useSeo'
import PageLayout from '@/components/layout/PageLayout.vue'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'Privacy Policy',
  description:
    'ClearRand privacy policy: no tracking of inputs, minimal analytics, and no account required.',
  path: '/privacy',
})

const commitments = [
  {
    icon: 'x-mark' as const,
    title: 'No account needed',
    description: 'Use every calculator without signing up.',
  },
  {
    icon: 'x-mark' as const,
    title: 'Inputs stay private',
    description: 'We do not store the numbers you enter.',
  },
  {
    icon: 'info' as const,
    title: 'Privacy-safe analytics',
    description: 'Only aggregated events, never raw data.',
  },
]
</script>

<template>
  <PageLayout
    title="Privacy policy"
    description="How we handle your data when you use our calculators."
    :breadcrumbs="[{ label: 'Privacy' }]"
  >
    <div class="section-heading">
      <span class="section-heading__eyebrow">Our promise</span>
      <h2 class="section-heading__title">Your data, your business</h2>
    </div>
    <div class="privacy-grid">
      <Card
        v-for="item in commitments"
        :key="item.title"
        padding="lg"
        raised
        accent="primary"
        hover
      >
        <div class="privacy-card__icon">
          <Icon :name="item.icon" :size="24" />
        </div>
        <h2 class="privacy-card__title">{{ item.title }}</h2>
        <p class="privacy-card__desc">{{ item.description }}</p>
      </Card>
    </div>

    <article class="prose">
      <p><strong>Last reviewed:</strong> 11 September 2026</p>
      <h2>What we collect</h2>
      <p>
        We do not require accounts and we do not store the numbers you enter into a calculator.
        Calculations happen in your browser. With your consent, we may remember harmless UI
        preferences (such as reduced motion) in your browser's local storage.
      </p>
      <h2>Analytics</h2>
      <p>
        If enabled, we send privacy-safe, aggregated events such as "calculator completed". We never
        send salary, property prices, income, names, emails or raw inputs.
      </p>
      <h2>Third parties</h2>
      <p>
        We do not sell or share your personal data. Any future partner or lead forms will only
        operate with clear consent and a stated purpose.
      </p>
      <h2>Changes</h2>
      <p>We will update this page if our practices change.</p>
    </article>
  </PageLayout>
</template>

<style scoped>
.section-heading {
  margin-bottom: var(--space-5);
}

.section-heading__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section-heading__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.privacy-grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-10);
}

@media (min-width: 640px) {
  .privacy-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.privacy-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.12), rgb(13 148 136 / 0.08));
  color: var(--color-primary);
  margin-bottom: var(--space-4);
}

.privacy-card__title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.privacy-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.prose {
  max-width: 70ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.prose p {
  margin-top: var(--space-3);
}
</style>
